"use client";

import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { MessageSquarePlus, Send, Loader2, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";
import { useTranslations } from "@/hooks/useTranslations";

interface FeedbackModalProps {
  trigger?: React.ReactNode;
  className?: string;
}

type FeedbackType = "feature" | "bug" | "improvement" | "other";

export function FeedbackModal({ trigger, className }: FeedbackModalProps) {
  const t = useTranslations("feedback");
  const [open, setOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    type: "" as FeedbackType | "",
    subject: "",
    message: "",
  });

  const feedbackTypes: { value: FeedbackType; label: string }[] = [
    { value: "feature", label: t("types.feature") },
    { value: "bug", label: t("types.bug") },
    { value: "improvement", label: t("types.improvement") },
    { value: "other", label: t("types.other") },
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      const response = await fetch("/api/feedback", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to submit feedback");
      }

      setIsSuccess(true);
      setFormData({ type: "", subject: "", message: "" });

      // Close modal after showing success
      setTimeout(() => {
        setOpen(false);
        setIsSuccess(false);
      }, 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    setOpen(newOpen);
    if (!newOpen) {
      // Reset form when closing
      setFormData({ type: "", subject: "", message: "" });
      setError(null);
      setIsSuccess(false);
    }
  };

  const defaultTrigger = (
    <button
      className={cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-200 min-h-[44px] text-purple-600 hover:bg-purple-50 dark:text-purple-400 dark:hover:bg-purple-950/20 w-full",
        className
      )}
    >
      <MessageSquarePlus className="h-5 w-5" />
      <span>{t("giveFeedback")}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>{trigger || defaultTrigger}</DialogTrigger>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageSquarePlus className="h-5 w-5 text-purple-600" />
            {t("title")}
          </DialogTitle>
          <DialogDescription>
            {t("description")}
          </DialogDescription>
        </DialogHeader>

        {isSuccess ? (
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <CheckCircle className="h-12 w-12 text-green-500 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {t("thankYou")}
            </h3>
            <p className="text-sm text-gray-500 mt-2">
              {t("thankYouDescription")}
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label htmlFor="feedback-type">{t("form.typeLabel")}</Label>
              <Select
                value={formData.type}
                onValueChange={(value: FeedbackType) =>
                  setFormData({ ...formData, type: value })
                }
              >
                <SelectTrigger id="feedback-type">
                  <SelectValue placeholder={t("form.typePlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {feedbackTypes.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-subject">{t("form.subjectLabel")}</Label>
              <Input
                id="feedback-subject"
                placeholder={t("form.subjectPlaceholder")}
                value={formData.subject}
                onChange={(e) =>
                  setFormData({ ...formData, subject: e.target.value })
                }
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="feedback-message">{t("form.messageLabel")}</Label>
              <Textarea
                id="feedback-message"
                placeholder={t("form.messagePlaceholder")}
                rows={5}
                value={formData.message}
                onChange={(e) =>
                  setFormData({ ...formData, message: e.target.value })
                }
                required
              />
            </div>

            {error && (
              <div className="text-sm text-red-600 bg-red-50 dark:bg-red-950/20 px-3 py-2 rounded-md">
                {error}
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setOpen(false)}
                disabled={isSubmitting}
              >
                {t("buttons.cancel")}
              </Button>
              <Button
                type="submit"
                disabled={isSubmitting || !formData.type || !formData.subject || !formData.message}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t("buttons.sending")}
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    {t("buttons.submit")}
                  </>
                )}
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
